import {
    currencyFormatter,
    formatPiecePricingFromCount,
} from "@utils/formatter";
import { PRODUCT_PLACEHOLDER_SRC } from "@utils/productImage";
import type { OrderProductResponse } from "@models/order";
import { recordedPiecesPerUnit } from "@utils/recordedPiecesPerUnit";

interface ConfirmationItemRowProps {
    product: OrderProductResponse;
}

export const ConfirmationItemRow = ({ product }: ConfirmationItemRowProps) => {
    // The order payload may omit line-item variants (and the API returns a single `variant`, not a
    // `variants` array — see GetMeOrder), so guard against undefined to avoid a render crash.
    const variants = product.variants ?? [];
    // #432: the line's price is its vendor offering's price — on a variant line already
    // the FULL variant price (CreateOrder snapshots OrderProduct.Price = VendorSku.Price) —
    // so a variant's own price is never added. Same rule as admin OrderDetail.
    const lineTotal = product.price * product.quantity;
    const pieces = recordedPiecesPerUnit(product.piecesPerUnit);
    const pricing = formatPiecePricingFromCount(product.price, pieces);

    return (
        <div
            data-testid="confirmation-item-row"
            className="border-t border-base-300 py-4 flex gap-3 md:gap-4"
        >
            {/* The order line carries its own image URL (null = no image). */}
            <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 self-start rounded-md bg-base-100 overflow-hidden flex items-center justify-center">
                <img
                    src={product.imageUrl || PRODUCT_PLACEHOLDER_SRC}
                    alt={product.name}
                    className="w-full h-full object-contain"
                />
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-1">
                <p className="font-bold text-neutral line-clamp-2">
                    {product.name}
                </p>

                {/* Orders 6: the pieces per unit the line was placed with, by
                    PR 438's admin rule. A piece price shows only for more than one
                    piece; an unknown count (0 or absent) shows the unit price
                    alone, is never divided, and reads "—" on the count line. */}
                <p className="text-sm text-neutral">
                    <span className="font-semibold">{pricing.unitPrice}</span>
                    <span className="text-neutral/70 ml-1">unit</span>
                    {pricing.showPiece && (
                        <>
                            <span className="text-neutral/40 mx-1">/</span>
                            <span className="font-semibold">
                                {pricing.piecePrice}
                            </span>
                            <span className="text-neutral/70 ml-1">piece</span>
                        </>
                    )}
                </p>
                <p className="text-xs text-neutral/70">
                    {`${pieces ?? "—"} ${pieces === 1 ? "piece" : "pieces"} per unit`}
                </p>

                {variants.length > 0 && (
                    <p className="text-xs text-neutral/70">
                        {variants.map((v) => v.name).join(", ")}
                    </p>
                )}

                <div className="flex items-center justify-between gap-3 mt-2">
                    <span className="text-sm font-semibold text-neutral">
                        Qty {product.quantity}
                    </span>
                    <span className="font-bold text-neutral">
                        {currencyFormatter.format(lineTotal)}
                    </span>
                </div>
            </div>
        </div>
    );
};
